import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../../../../../util/api";

// MUI
import {
    Box,
    Chip,
    Dialog,
    IconButton,
    Paper,
    Stack,
    Typography,
    Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import EditKeyVisualModal from "./EditKeyVisualModal";
import ActionButton from "../../../../../component/common/ActionButton";
import { ds } from "../../../../../styles/designSystem";
import { confirm as confirmDialog } from "../../../../../util/confirmDialog";
import { safeImgUrl } from "../../../../../util/imageUrl";
import { assetDisplayName } from "../AssetPicker";

/**
 * 로고·레퍼런스 한 줄 — 이름 칩을 누르면 새 탭으로 원본이 열린다.
 *
 * <p>키비주얼 본체와 달리 <b>워터마크 다운로드 엔드포인트를 타지 않는다.</b>
 * BI·CI 는 원본 그대로 써야 하는 파일이라 서버가 워터마크를 넣지 않고, 값도 CDN URL 이라
 * 링크로 바로 연다(본체는 {@code /download/{id}} 로 워터마크를 입혀 내려받는다).
 */
function AssetLinkRow({ label, urls }) {
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap flexWrap="wrap">
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary", pt: 0.3 }}>
                {label}
            </Typography>
            {urls.map((u) => (
                <Chip
                    key={u}
                    size="small"
                    label={assetDisplayName(u)}
                    clickable
                    onClick={() => window.open(u, "_blank", "noopener,noreferrer")}
                    sx={{ ...ds.components.external.chip, maxWidth: 240 }}
                />
            ))}
        </Stack>
    );
}

export default function KeyVisualModal({
                                            keyVisual,
                                            onClose,
                                            onUpdate,
                                            onDeleted,
                                            openSnack,
                                        }) {
    const [showEditModal, setShowEditModal] = useState(false);
    const [busy, setBusy] = useState(false);

    // 외부 props 변경(키워드/프로젝트명 등 부분 갱신 포함)에 즉시 동기화
    const [localItem, setLocalItem] = useState(keyVisual);

    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const API_REAL = process.env.REACT_APP_API_REAL_URL;

    useEffect(() => {
        setLocalItem(keyVisual);
    }, [keyVisual]);

    const imgUrl = useMemo(() => {
        const fp = localItem?.filePath || "";
        if (!fp) return "";
        const absolute = (fp.startsWith("http://") || fp.startsWith("https://"))
            ? fp
            : `${API_REAL}/${fp}`.replaceAll("//", "/").replace(":/", "://");
        return safeImgUrl(absolute);
    }, [API_REAL, localItem]);

    const handleDownload = useCallback(async () => {
        if (!localItem?.id) return;
        setBusy(true);
        try {
            // api.download 는 Blob 이 아니라 axios 응답 객체를 준다 (http.js:19 — Content-Disposition
            // 을 읽어야 하는 호출부 때문에 의도적으로 res 를 반환). .data 를 벗기지 않으면
            // Blob 생성자가 객체를 문자열화해 "[object Object]" 15바이트 파일이 저장된다.
            const res  = await api.download(`${API_BASE}/key-visual/2d/download/${localItem.id}`);
            const blob = res?.data ?? res;

            // 이미 Blob 이라 재포장하지 않는다 — 키비주얼은 대형 원본이라
            // new Blob([blob]) 로 감싸면 통째로 메모리 복사가 한 번 더 일어난다.
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", (localItem?.name || "key-visual") + ".png");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            openSnack?.("다운로드 시작", "success");
        } catch (e) {
            console.error(e);
            openSnack?.("파일 다운로드에 실패했습니다.", "error");
        } finally {
            setBusy(false);
        }
    }, [API_BASE, localItem, openSnack]);

    const handleDelete = useCallback(async () => {
        const ok = (await confirmDialog({ message: "정말 삭제하시겠습니까?" }));
        if (!ok || !localItem?.id) return;

        setBusy(true);
        try {
            await api.delete(`${API_BASE}/key-visual/2d/delete/${localItem.id}`);
            onDeleted?.();
        } catch (e) {
            console.error(e);
            openSnack?.("삭제하는데 실패했습니다.", "error");
        } finally {
            setBusy(false);
        }
    }, [API_BASE, localItem, onDeleted, openSnack]);

    if (!localItem) return null;

    return (
        <>
            <Dialog
                open
                disableEscapeKeyDown
                onClose={(e, reason) => {
                    onClose();
                }}
                fullWidth={true}
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        borderRadius: 1.5,
                        border: `1px solid ${ds.colors.border}`,
                        boxShadow: "0 18px 60px rgba(15, 23, 42, 0.18)",
                        bgcolor: ds.colors.surface,
                        display: "flex",
                        height: "92vh",            // 모달 높이 고정 (뷰어 스크롤을 위해)
                        maxHeight: "92vh",
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        bgcolor: "#fff",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            noWrap
                            sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}
                            title={localItem?.name || "키비주얼"}
                        >
                            {localItem?.name || "키비주얼"}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.2 }}>
                            {localItem?.projectName && (
                                <Chip
                                    label={localItem.projectName}
                                    size="small"
                                    sx={ds.components.external.chip}
                                />
                            )}
                            <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#64748b" }}>
                                키워드 / 미리보기 / 다운로드
                            </Typography>
                        </Stack>
                    </Box>

                    <ActionButton
                        variant="download"
                        onClick={handleDownload}
                        loading={busy}
                        tooltip="클릭 시, 키비주얼을 다운로드 합니다"
                    />

                    <Tooltip title="닫기">
                        <IconButton size="small" onClick={onClose} sx={{ color: "#64748b" }}>
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Keywords */}
                <Box sx={{ px: 2.5, py: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        {(localItem?.keywords || []).length > 0 ? (
                            localItem.keywords.map((k, i) => (
                                <Chip
                                    key={`${k}-${i}`}
                                    label={String(k)}
                                    size="small"
                                    variant="outlined"
                                    sx={ds.components.external.chip}
                                />
                            ))
                        ) : (
                            <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: 13 }}>
                                등록된 키워드가 없습니다.
                            </Typography>
                        )}
                    </Stack>

                    {/* 로고·레퍼런스 — 있을 때만. 없는 항목까지 빈 줄로 자리를 차지하면
                        대부분의 기존 키비주얼에서 화면만 길어진다. */}
                    {((localItem?.logoPaths || []).length > 0 || (localItem?.referencePaths || []).length > 0) && (
                        <Stack spacing={1} sx={{ mt: 1.5 }}>
                            {(localItem?.logoPaths || []).length > 0 && (
                                <AssetLinkRow label="로고 (BI·CI)" urls={localItem.logoPaths} />
                            )}
                            {(localItem?.referencePaths || []).length > 0 && (
                                <AssetLinkRow label="레퍼런스" urls={localItem.referencePaths} />
                            )}
                        </Stack>
                    )}
                </Box>


                {/* Image viewer (가로=모달 폭 고정, 세로=스크롤) */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        p: 2.5,
                        bgcolor: ds.colors.surface,
                        overflowY: "auto",     // 세로 스크롤
                        overflowX: "hidden",   // 가로 스크롤 방지
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 1.5,
                            borderColor: ds.colors.border,
                            bgcolor: ds.colors.bg,
                            overflow: "hidden",
                        }}
                    >
                        {imgUrl ? (
                            <Box
                                component="img"
                                src={imgUrl}
                                alt={localItem?.name || "key-visual"}
                                sx={{
                                    display: "block",
                                    width: "100%",      // 가로는 무조건 모달 폭에 맞춤
                                    height: "auto",     // 비율 유지
                                    userSelect: "none",
                                }}
                            />
                        ) : (
                            <Box sx={{ p: 6, textAlign: "center", color: "text.secondary", fontWeight: 900 }}>
                                이미지 경로가 없습니다.
                            </Box>
                        )}
                    </Paper>
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        borderTop: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "#fafbfc",
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ flex: 1 }} />

                    {onUpdate && (
                        <ActionButton
                            variant="edit"
                            onClick={() => setShowEditModal(true)}
                            disabled={busy}
                        />
                    )}

                    {onDeleted && (
                        <ActionButton
                            variant="delete"
                            onClick={handleDelete}
                            disabled={busy}
                        />
                    )}

                    <ActionButton variant="close" onClick={onClose} />
                </Box>
            </Dialog>

            {/* EditKeyVisualModal */}
            {showEditModal && (
                <EditKeyVisualModal
                    open={showEditModal}
                    keyVisual={localItem}
                    onClose={() => setShowEditModal(false)}
                    openSnack={openSnack}
                    onSaved={(updated) => {
                        setLocalItem(updated);
                        onUpdate?.(updated);
                        setShowEditModal(false);
                    }}
                />
            )}
        </>
    );
}
